import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { TaskPriorityOptions } from 'shared/types/task-base';
import { getPlatformShortcut } from 'shared/utils/get-platform-shortcut';

export type PriorityPickerMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'onOutside'
> & {
  value?: TaskPriorityOptions | null;
  onClose: () => void;
  onChange: (value: TaskPriorityOptions | null) => void;
};

export const PriorityPickerMenu: React.FC<PriorityPickerMenuProps> = ({
  value,
  onChange,
  onClose,
  ...rest
}) => {
  const { t } = useTranslation();

  const onClear = () => {
    onChange(null);
    onClose();
  };

  const onHigh = () => {
    onChange(TaskPriorityOptions.High);
    onClose();
  };

  const onMedium = () => {
    onChange(TaskPriorityOptions.Medium);
    onClose();
  };

  const onLow = () => {
    onChange(TaskPriorityOptions.Low);
    onClose();
  };
  const menuRef = React.useRef<HTMLDivElement>(null);

  const focusItem = React.useCallback((direction: 'next' | 'previous') => {
    if (!menuRef.current) {
      return;
    }

    const buttons = Array.from(
      menuRef.current.querySelectorAll('button:not([disabled])'),
    ) as HTMLElement[];

    if (!buttons.length) {
      return;
    }

    const currentIndex = buttons.indexOf(document.activeElement as HTMLElement);
    const nextIndex =
      direction === 'next'
        ? (currentIndex + 1) % buttons.length
        : (currentIndex - 1 + buttons.length) % buttons.length;

    buttons[nextIndex]?.focus();
  }, []);

  useKeysDown(
    ['ArrowDown'],
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      focusItem('next');
    },
    { capture: true },
  );

  useKeysDown(
    ['ArrowUp'],
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      focusItem('previous');
    },
    { capture: true },
  );
  return (
    <PopupMenu ref={menuRef} onOutside={onClose} {...rest}>
      <PopupMenuList>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onHigh}
            start={
              value === TaskPriorityOptions.High ? (
                <Icon icon={Check} />
              ) : undefined
            }
            shortcut={getPlatformShortcut(['Shift', '1'])}
          >
            {t('task.priority.options.high.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onMedium}
            start={
              value === TaskPriorityOptions.Medium ? (
                <Icon icon={Check} />
              ) : undefined
            }
            shortcut={getPlatformShortcut(['Shift', '2'])}
          >
            {t('task.priority.options.medium.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onLow}
            start={
              value === TaskPriorityOptions.Low ? (
                <Icon icon={Check} />
              ) : undefined
            }
            shortcut={getPlatformShortcut(['Shift', '3'])}
          >
            {t('task.priority.options.low.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onClear}
            start={value === null ? <Icon icon={Check} /> : undefined}
            shortcut={getPlatformShortcut(['Shift', '4'])}
          >
            {t('task.priority.options.none.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </PopupMenu>
  );
};
